import { CdkTrapFocus } from '@angular/cdk/a11y';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { NotificationListComponent } from './notification-list.component';
import { NotificationsService } from './notifications.service';
import { NotificationNavigationKindEnum, type Notification } from './notifications.types';
import { formatNotificationCount, resolveNotificationNavigation } from './notifications.util';

@Component({
  selector: 'notifications',
  template: `
    <button
      type="button"
      mat-icon-button
      class="notifications-toggle"
      aria-label="Notificaties openen"
      aria-haspopup="dialog"
      aria-controls="notifications-panel"
      [attr.aria-expanded]="panelOpen()"
      (click)="togglePanel()"
      #notificationsOrigin
      data-cy="notifications-toggle">
      <span
        class="notification-asset-icon notification-asset-icon--bell notifications-toggle__icon"
        aria-hidden="true"></span>
      @if (notificationsService.unreadCount() > 0) {
        <span class="notifications-toggle__badge" data-cy="notifications-unread-count">
          {{ unreadCountDisplay() }}
        </span>
      }
    </button>

    <ng-template #notificationsPanel>
      <section
        id="notifications-panel"
        class="notifications-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Notificaties"
        cdkTrapFocus
        [cdkTrapFocusAutoCapture]="true"
        data-cy="notifications-panel">
        <header class="notifications-panel__header">
          <h2>Notificaties</h2>
          <button
            type="button"
            class="notifications-panel__mark-all"
            aria-label="Alles als gelezen markeren"
            [disabled]="
              (!notificationsService.hasMoreEntries() && notificationsService.unreadCount() === 0) ||
              notificationsService.loading() ||
              notificationsService.markingAllAsRead()
            "
            (click)="notificationsService.markAllAsRead()"
            data-cy="notifications-panel-mark-all">
            <span
              class="notification-asset-icon notification-asset-icon--check-check notifications-panel__mark-all-icon"
              aria-hidden="true"></span>
            <span>Markeren als gelezen</span>
          </button>
        </header>

        @if (notificationsService.markAllAsReadFailed()) {
          <div class="notifications-panel__mark-all-error" role="alert">Markeren mislukt. Probeer opnieuw.</div>
        }

        <app-notification-list
          class="notifications-panel__list"
          (selected)="handleNotificationClick($event)"></app-notification-list>

        <footer class="notifications-panel__footer">
          <button
            type="button"
            class="notifications-panel__view-all"
            (click)="viewAllNotifications()"
            data-cy="notifications-view-all">
            <span>Alle notificaties bekijken</span>
            <span
              class="notification-asset-icon notification-asset-icon--arrow-right notifications-panel__view-all-icon"
              aria-hidden="true"></span>
          </button>
        </footer>
      </section>
    </ng-template>
  `,
  styles: `
    notifications {
      display: block;
      height: 36px;
      width: 36px;
    }

    notifications .notifications-toggle.mat-mdc-icon-button {
      --mdc-icon-button-state-layer-size: 36px;
      align-items: center;
      border-radius: 8px;
      display: flex;
      height: 36px;
      justify-content: center;
      padding: 8px;
      transition: background-color 120ms ease;
      width: 36px;
    }

    notifications .notifications-toggle.mat-mdc-icon-button:hover,
    notifications .notifications-toggle.mat-mdc-icon-button:focus-visible {
      background: rgba(0, 0, 0, 0.08);
    }

    .dark notifications .notifications-toggle.mat-mdc-icon-button:hover,
    .dark notifications .notifications-toggle.mat-mdc-icon-button:focus-visible {
      background: rgba(255, 255, 255, 0.15);
    }

    notifications .notifications-toggle__icon {
      height: 20px;
      width: 20px;
    }

    .notification-asset-icon {
      background: currentColor;
      display: inline-block;
      flex: none;
      mask-position: center;
      mask-repeat: no-repeat;
      mask-size: contain;
      -webkit-mask-position: center;
      -webkit-mask-repeat: no-repeat;
      -webkit-mask-size: contain;
    }

    .notification-asset-icon--bell {
      mask-image: url('/assets/icons/notifications/bell.svg');
      mask-size: 75% 83.333%;
      -webkit-mask-image: url('/assets/icons/notifications/bell.svg');
      -webkit-mask-size: 75% 83.333%;
    }

    .notification-asset-icon--check-check {
      mask-image: url('/assets/icons/notifications/check-check.svg');
      mask-size: 83.333% 48%;
      -webkit-mask-image: url('/assets/icons/notifications/check-check.svg');
      -webkit-mask-size: 83.333% 48%;
    }

    .notification-asset-icon--arrow-right {
      mask-image: url('/assets/icons/notifications/arrow-right.svg');
      mask-size: 80%;
      -webkit-mask-image: url('/assets/icons/notifications/arrow-right.svg');
      -webkit-mask-size: 80%;
    }

    notifications .notifications-toggle__badge {
      align-items: center;
      background: #d42e11;
      border-radius: 9999px;
      color: #ffffff;
      display: flex;
      font-size: 10px;
      font-variant-numeric: tabular-nums;
      font-weight: 500;
      height: 16px;
      justify-content: center;
      left: 16px;
      line-height: 1.3;
      min-width: 16px;
      padding: 2px 4px;
      pointer-events: none;
      position: absolute;
      top: 2px;
      z-index: 2;
    }

    .dark notifications .notifications-toggle__badge {
      background: #ff7a61;
      color: #000000;
    }

    .notifications-panel {
      background: #ffffff;
      border-radius: 12px;
      box-shadow: 0 24px 48px -12px rgba(18, 18, 18, 0.18);
      color: #000000;
      display: flex;
      flex-direction: column;
      height: min(541px, calc(100vh - 24px));
      overflow: hidden;
      width: min(380px, calc(100vw - 24px));
    }

    .dark .notifications-panel {
      background: #1e1e1e;
      box-shadow: 0 24px 48px -12px rgba(30, 30, 30, 0.64);
      color: #ffffff;
    }

    .notifications-panel__header {
      align-items: center;
      background: #eeeff1;
      display: flex;
      flex: 0 0 52px;
      gap: 10px;
      justify-content: space-between;
      padding: 16px 12px;
    }

    .dark .notifications-panel__header {
      background: #2c2c2e;
    }

    .notifications-panel__header h2 {
      font-size: 15px;
      font-weight: 500;
      line-height: 1.3;
      margin: 0;
    }

    .notifications-panel__mark-all {
      align-items: center;
      background: transparent;
      border: 0;
      border-radius: 8px;
      color: inherit;
      cursor: pointer;
      display: flex;
      font-size: 14px;
      font-weight: 500;
      gap: 4px;
      justify-content: center;
      line-height: 1.4;
      padding: 0;
      white-space: nowrap;
    }

    .notifications-panel__mark-all > span:last-child {
      border-bottom: 1px solid currentColor;
    }

    .notifications-panel__mark-all:disabled {
      cursor: default;
      opacity: 0.45;
    }

    .notifications-panel__mark-all-icon {
      height: 20px;
      width: 20px;
    }

    .notifications-panel__mark-all-error {
      color: #d42e11;
      flex: none;
      font-size: 12px;
      padding: 8px 12px 0;
    }

    .dark .notifications-panel__mark-all-error {
      color: #ff7a61;
    }

    .notifications-panel__list {
      display: block;
      flex: 1 1 auto;
      min-height: 0;
    }

    .notifications-panel__footer {
      align-items: center;
      background: #ffffff;
      display: flex;
      flex: 0 0 64px;
      padding: 16px 12px;
    }

    .dark .notifications-panel__footer {
      background: #1e1e1e;
    }

    .notifications-panel__view-all {
      align-items: center;
      background: #eeeff1;
      border: 0;
      border-radius: 8px;
      color: #000000;
      cursor: pointer;
      display: flex;
      font-size: 14px;
      font-weight: 500;
      gap: 12px;
      height: 32px;
      justify-content: center;
      line-height: 1.4;
      padding: 0 10px;
      transition: background-color 120ms ease;
      width: 100%;
    }

    .notifications-panel__view-all:hover,
    .notifications-panel__view-all:focus-visible {
      background: #dfe0e3;
    }

    .dark .notifications-panel__view-all {
      background: #545559;
      color: #ffffff;
    }

    .dark .notifications-panel__view-all:hover,
    .dark .notifications-panel__view-all:focus-visible {
      background: #626368;
    }

    .notifications-panel__view-all-icon {
      height: 20px;
      width: 20px;
    }

    @media (max-width: 639px) {
      .notifications-panel {
        height: calc(100vh - 24px);
      }

      .notifications-panel__mark-all {
        font-size: 12px;
      }
    }
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CdkTrapFocus, MatButtonModule, NotificationListComponent],
})
export class NotificationsComponent implements OnDestroy {
  @ViewChild('notificationsOrigin', { read: ElementRef })
  private notificationsOrigin?: ElementRef<HTMLButtonElement>;

  @ViewChild('notificationsPanel')
  private notificationsPanel?: TemplateRef<unknown>;

  readonly notificationsService = inject(NotificationsService);
  readonly panelOpen = signal(false);
  readonly unreadCountDisplay = computed(() =>
    formatNotificationCount(
      this.notificationsService.unreadCount(),
      this.notificationsService.hasMoreUnreadEntries()
    )
  );

  readonly #overlay = inject(Overlay);
  readonly #router = inject(Router);
  readonly #viewContainerRef = inject(ViewContainerRef);
  private overlayRef?: OverlayRef;

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }

  handleNotificationClick(notification: Notification): void {
    if (!notification.entry.read) {
      this.notificationsService.toggleRead(notification);
    }

    this.closePanel();
    const navigation = resolveNotificationNavigation(notification.entry.link, window.location.origin);
    if (navigation.kind === NotificationNavigationKindEnum.None) {
      void this.navigateToNotifications();
      return;
    }

    if (navigation.kind === NotificationNavigationKindEnum.Internal) {
      void this.#router.navigateByUrl(navigation.url);
      return;
    }

    if (navigation.kind === NotificationNavigationKindEnum.External) {
      window.location.assign(navigation.url);
      return;
    }

    void this.navigateToNotifications();
  }

  togglePanel(): void {
    if (this.panelOpen()) {
      this.closePanel();
      return;
    }

    this.openPanel();
  }

  openPanel(): void {
    if (!this.notificationsPanel || !this.notificationsOrigin || this.overlayRef?.hasAttached()) {
      return;
    }

    this.notificationsService.refreshUnreadCount();
    this.overlayRef ??= this.createOverlay();
    this.overlayRef.attach(new TemplatePortal(this.notificationsPanel, this.#viewContainerRef));
    this.panelOpen.set(true);
  }

  closePanel(): void {
    this.overlayRef?.detach();
    this.panelOpen.set(false);
    this.notificationsOrigin?.nativeElement.focus();
  }

  viewAllNotifications(): void {
    this.closePanel();
    void this.navigateToNotifications();
  }

  private createOverlay(): OverlayRef {
    const overlayRef = this.#overlay.create({
      hasBackdrop: true,
      backdropClass: 'fuse-backdrop-on-mobile',
      scrollStrategy: this.#overlay.scrollStrategies.block(),
      positionStrategy: this.#overlay
        .position()
        .flexibleConnectedTo(this.notificationsOrigin!.nativeElement)
        .withLockedPosition(true)
        .withPush(true)
        .withPositions([
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
            offsetY: 7,
          },
          {
            originX: 'end',
            originY: 'top',
            overlayX: 'end',
            overlayY: 'bottom',
            offsetY: -7,
          },
        ]),
    });

    overlayRef.backdropClick().subscribe(() => this.closePanel());
    overlayRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closePanel();
      }
    });
    overlayRef.detachments().subscribe(() => this.panelOpen.set(false));
    return overlayRef;
  }

  private navigateToNotifications(): Promise<boolean> {
    const slug = this.notificationsService.entity()?.slug;
    return slug ? this.#router.navigate(['/', slug]) : Promise.resolve(false);
  }
}
